/**
 * Loads what members ACTUALLY composed with — the read side of migration 570.
 *
 * Same shape as useStylesData (co-located client, one useEffect, no global
 * store), and deliberately NOT the shape of the LifeScribe original's usage
 * hook: that one manufactured its "Usage Over Time" series with `Math.random()`
 * (`../lifescribe` useStylesData.ts:164-171), regenerated on every render, so
 * its trend changed if you blinked. Nothing here invents a number.
 *
 * FIVE STATES, BECAUSE THERE ARE FIVE DIFFERENT FACTS:
 *
 *   loading        — we do not know yet.
 *   error          — the request FAILED. Not an empty result; a failed fetch and
 *                    "nobody used anything" are not the same fact and must not
 *                    draw the same.
 *   not-recording  — 503 STYLE_USAGE_MIGRATION_NOT_APPLIED: migration 570 has
 *                    not been applied in this database, so nothing anywhere is
 *                    recording. Its own state, not an error and not a zero.
 *   empty          — the table is there and has no rows (`since: null`). "Not
 *                    recorded yet", never "0 uses".
 *   ready          — real rows, and `since` says how far back they go.
 *
 * The empty/not-recording split is the whole point: a style nobody has used and
 * a style used before recording existed are different facts, and so are an
 * empty table and a database that never got the table.
 */

import { useCallback, useEffect, useState } from 'react';

import { QuillApiError } from '../../../services/quillAdminService';
import { STYLE_USAGE_MIGRATION_NOT_APPLIED, styleUsageApi } from './api';
import { DEFAULT_USAGE_WINDOW_DAYS } from './constants';
import { StyleUsageResponse } from './types';

export type StyleUsageState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'not-recording'; message: string }
  | { kind: 'empty'; days: number }
  | { kind: 'ready'; data: StyleUsageResponse };

export interface UseStyleUsageResult {
  usage: StyleUsageState;
  reload: () => Promise<void>;
}

export function useStyleUsage(days: number = DEFAULT_USAGE_WINDOW_DAYS): UseStyleUsageResult {
  const [usage, setUsage] = useState<StyleUsageState>({ kind: 'loading' });

  const load = useCallback(
    async (isCurrent: () => boolean) => {
      try {
        const data = await styleUsageApi.get(days);
        if (!isCurrent()) return;
        // `since` is the floor for every number in this response. Without one
        // there is nothing to be "since", so there is nothing to count — and a
        // zero here would read as a measurement rather than as an absence.
        if (!data.since || data.total === 0) {
          setUsage({ kind: 'empty', days: data.window.days || days });
          return;
        }
        setUsage({ kind: 'ready', data });
      } catch (e) {
        if (!isCurrent()) return;
        if (e instanceof QuillApiError && e.code === STYLE_USAGE_MIGRATION_NOT_APPLIED) {
          setUsage({ kind: 'not-recording', message: e.message });
          return;
        }
        setUsage({
          kind: 'error',
          message: e instanceof Error ? e.message : 'Failed to load style usage.',
        });
      }
    },
    [days]
  );

  const reload = useCallback(async () => {
    setUsage({ kind: 'loading' });
    await load(() => true);
  }, [load]);

  useEffect(() => {
    let current = true;
    load(() => current);
    return () => {
      current = false;
    };
  }, [load]);

  return { usage, reload };
}

export default useStyleUsage;
